import { Icon } from "./Icon";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { voiceBucket } from "../routes/inboxSurfaces";
import {
  ceilingFor, containerLabel, emptyRec, recorder, REVIEW_MEDIA_BUCKET,
  type MediaHome, type MediaKind, type Mode, type RecEvent, type RecState, type SlotKey,
} from "../lib/reviewMedia";
import type { Remove, Upload } from "../lib/mediaUpload";
import { useVoiceRecording, type VoiceRecording } from "./VoiceRecorder";
import { VoiceNote } from "./VoiceNote";
import { RecordingBubble, RoundVideoPlayer, useSignedUrl, useVideoRecording, type VideoRecording } from "./RoundVideo";
import { MediaStage } from "./MediaStage";
import { toReviewJpeg } from "../lib/reviewPhoto";

/* W82 STEP 5 — THE REVIEW'S MEDIA ON THE WEB (his §8 and §11, 2026-09-26).
 *
 * THE DRAFT'S MEDIA are drawn by the composer (`ReviewComposer.tsx`, job 3 step 5 page 2 — his 10-07 composer, the
 * phone's names): ONE of each kind per comment, the overall's video and live review sharing the review's ONE video. This
 * file holds the recorder they all drive and the SENT takes' players.
 *
 * THE ONE RECORDER (`useReviewRecorder`): the whole page has ONE — tapping Record on another exercise STOPS the running
 * one (its take lands in memory at once) and starts the next (`reviewMedia.recorder`, the pure state machine). The voice
 * engine is the Inbox's own `useVoiceRecording`; the video engine `useVideoRecording`. NOTHING IS UPLOADED HERE: the takes
 * are Blobs in this tab until Send, whose queue (`mediaUpload.runUploads`) the page runs after the undo window.
 * THE × ON A SENT TAKE is its OWNER removing the OBJECT (his bound (1); the bucket's own-folder DELETE) — the row keeps its
 * path and every player then reads it as removed.
 */

/** The review bucket's two calls, as the coach (the bucket's own-folder INSERT and DELETE decide; nothing here widens). */
export const reviewUpload: Upload = async (path, blob, contentType) => {
  const { error } = await supabase.storage.from(REVIEW_MEDIA_BUCKET).upload(path, blob, { contentType, upsert: false });
  return error === null ? null : error.message;
};
export const reviewRemove: Remove = async (paths) => {
  const { error } = await supabase.storage.from(REVIEW_MEDIA_BUCKET).remove(paths);
  return error === null ? null : error.message;
};

export type ReviewRecorder = {
  rec: RecState;
  notice: string | null;
  voice: VoiceRecording;
  video: VideoRecording;
  toggle: (slot: SlotKey, mode: Mode) => void;
  remove: (slot: SlotKey, kind: MediaKind) => void;
  /** 2026-10-07 — a picked photo becomes the slot's ONE photo (replacing any before it); a string = why not. */
  addPhoto: (slot: SlotKey, file: Blob) => Promise<string | null>;
  /** Stop whatever records (Send stops it first, so a take in flight is not lost). */
  stopAll: () => void;
  blobOf: (ref: string) => Blob | undefined;
  urlOf: (ref: string) => string | undefined;
  clear: (slots: readonly SlotKey[]) => void;
};

const tagOf = (slot: SlotKey, kind: MediaKind) => `${kind}|${slot}`;
const untag = (tag: string): { slot: SlotKey; kind: MediaKind } => {
  const i = tag.indexOf("|");
  return { kind: tag.slice(0, i) === "voice" ? "voice" : "video", slot: tag.slice(i + 1) };
};

export function useReviewRecorder(): ReviewRecorder {
  const [rec, setRec] = useState<RecState>(emptyRec);
  const recRef = useRef<RecState>(emptyRec);
  const blobs = useRef(new Map<string, { blob: Blob; url: string }>());
  const [notice, setNotice] = useState<string | null>(null);

  function apply(ev: RecEvent) {
    const step = recorder(recRef.current, ev);
    recRef.current = step.state;
    setRec(step.state);
    return step;
  }
  function land(tag: string, blob: Blob, seconds: number, live: boolean) {
    const { slot, kind } = untag(tag);
    const ref = crypto.randomUUID();
    blobs.current.set(ref, { blob, url: URL.createObjectURL(blob) });
    apply({ type: "finish", slot, kind, take: { ref, seconds, live } });
  }
  const voice = useVoiceRecording({ onTake: (t) => land(t.tag, t.blob, t.seconds, false) });
  const video = useVideoRecording({ onTake: (t) => land(t.tag, t.blob, t.seconds, t.live) });

  // The object URLs go with the page.
  useEffect(() => () => { for (const b of blobs.current.values()) URL.revokeObjectURL(b.url); }, []);

  function toggle(slot: SlotKey, mode: Mode) {
    const kind: MediaKind = mode === "voice" ? "voice" : "video";
    setNotice(null);
    const step = apply({ type: "start", slot, kind, live: mode === "live", atMs: Date.now() });
    if (step.refused !== null) { setNotice(step.refused); return; }
    if (step.stop !== null) (step.stop.kind === "voice" ? voice : video).release();
    const a = step.state.active;
    if (a === null || a.slot !== slot || a.kind !== kind) return;   // it was the Stop
    const started = kind === "voice"
      ? voice.start({ tag: tagOf(slot, kind), ceilingSeconds: ceilingFor(slot, "voice") })
      : video.start({ tag: tagOf(slot, kind), ceilingSeconds: ceilingFor(slot, mode), live: mode === "live" });
    void started.then((ok) => {
      const now = recRef.current.active;
      if (!ok && now !== null && now.slot === slot && now.kind === kind) apply({ type: "stop" });
    });
  }
  function remove(slot: SlotKey, kind: MediaKind) {
    const t = recRef.current.slots[slot]?.[kind];
    if (t !== undefined) {
      const b = blobs.current.get(t.ref);
      if (b !== undefined) URL.revokeObjectURL(b.url);
      blobs.current.delete(t.ref);
    }
    apply({ type: "remove", slot, kind });
  }
  async function addPhoto(slot: SlotKey, file: Blob): Promise<string | null> {
    const jpeg = await toReviewJpeg(file);
    if (typeof jpeg === "string") { setNotice(jpeg); return jpeg; }
    remove(slot, "photo");
    const ref = crypto.randomUUID();
    blobs.current.set(ref, { blob: jpeg, url: URL.createObjectURL(jpeg) });
    apply({ type: "finish", slot, kind: "photo", take: { ref, seconds: 0, live: false } });
    setNotice(null);
    return null;
  }
  function stopAll() {
    const a = recRef.current.active;
    if (a === null) return;
    apply({ type: "stop" });
    (a.kind === "voice" ? voice : video).release();
  }
  function clear(slots: readonly SlotKey[]) {
    for (const s of slots) for (const k of ["voice", "video", "photo"] as const) {
      const t = recRef.current.slots[s]?.[k];
      if (t !== undefined) { const b = blobs.current.get(t.ref); if (b !== undefined) URL.revokeObjectURL(b.url); blobs.current.delete(t.ref); }
      if (t !== undefined) apply({ type: "remove", slot: s, kind: k });
    }
  }
  return {
    rec, notice, voice, video, toggle, remove, addPhoto, stopAll, clear,
    blobOf: (ref) => blobs.current.get(ref)?.blob,
    urlOf: (ref) => blobs.current.get(ref)?.url,
  };
}

/** The recorder's floating face bubble (video and live review) — drawn once per page. */
export function ReviewRecordingBubble({ r }: { r: ReviewRecorder }) {
  const a = r.rec.active;
  if (a === null || a.kind !== "video" || !r.video.recording) return null;
  return <RecordingBubble stream={r.video.preview} live={r.video.live} elapsed={r.video.elapsed} ceiling={ceilingFor(a.slot, a.live ? "live" : "video")} />;
}

/** ONE container: its words (or its player), and — when filled — an × at its top-left. */
export function MediaBox({ state, label, onMain, onRemove, removeLabel, disabled, title, children, wide }: {
  state: string; label?: string; onMain?: () => void; onRemove?: (() => void) | null; removeLabel?: string;
  disabled?: boolean; title?: string; children?: ReactNode; wide?: boolean;
}) {
  return (
    <div className={`rm-box is-${state}${wide === true ? " is-wide" : ""}`}>
      {children ?? (
        <button type="button" className="rm-main" onClick={onMain} disabled={disabled} title={title} aria-pressed={state === "recording"}>
          {label}
        </button>
      )}
      {onRemove != null && (
        <button type="button" className="rm-x" onClick={onRemove} aria-label={removeLabel ?? "Remove"}>
          <Icon name="x" />
        </button>
      )}
    </div>
  );
}

export type SentTakes = {
  id: string; senderId: string; audioPath: string | null; audioSeconds: number | null; videoPath: string | null; videoSeconds: number | null;
  /** 2026-10-07 — the row's photo (`photo_path`); null when none. */
  photoPath: string | null;
};

/** A SENT row's takes as containers: the voice plays inline, the video opens its player under the row; the SENDER gets the
 *  × (his bound (1): the owner removes the OBJECT from the bucket, the row keeps its path) after a one-line confirm. */
export function SentMedia({ row, home, viewerId, senderIsCoach, stage = false }: {
  row: SentTakes; home: MediaHome; viewerId: string; senderIsCoach: boolean;
  /** W93: the video opens CIRCULAR in the middle of the screen (`MediaStage`) instead of under the row. */
  stage?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [confirm, setConfirm] = useState<MediaKind | null>(null);
  const [gen, setGen] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mine = row.senderId.toLowerCase() === viewerId.toLowerCase();
  const words = mine ? "You removed this" : senderIsCoach ? "Removed by your coach" : "Removed by your client";
  const voiceBucketHere = home === "review" ? REVIEW_MEDIA_BUCKET : voiceBucket;
  async function removeObject(kind: MediaKind) {
    const path = kind === "voice" ? row.audioPath : kind === "video" ? row.videoPath : row.photoPath;
    if (path === null) return;
    const { error: e } = await supabase.storage.from(kind === "voice" ? voiceBucketHere : REVIEW_MEDIA_BUCKET).remove([path]);
    setConfirm(null);
    if (e !== null) { setError(e.message); return; }
    setOpen(false);
    setPhotoOpen(false);
    setGen((g) => g + 1);
  }
  // Only the sender may remove, and only from the review bucket (an Inbox note in `coach-voice` has no delete).
  const canRemove = (kind: MediaKind) => mine && (kind !== "voice" || home === "review");
  // The video is signed on mount, so its container can say `Removed` before anyone opens it.
  const signed = useSignedUrl(REVIEW_MEDIA_BUCKET, row.videoPath, gen);
  const videoState = signed.missing ? "removed" : "filled";
  const photo = useSignedUrl(REVIEW_MEDIA_BUCKET, row.photoPath, gen);
  if (row.audioPath === null && row.videoPath === null && row.photoPath === null) return null;
  return (
    <>
      {row.audioPath !== null && (
        <MediaBox state="filled" onRemove={canRemove("voice") ? () => setConfirm("voice") : null} removeLabel="Remove the voice note">
          <VoiceNote key={`v${String(gen)}`} compact enabled path={row.audioPath} seconds={row.audioSeconds ?? 0} bucket={voiceBucketHere} removedWords={words} />
        </MediaBox>
      )}
      {row.videoPath !== null && (
        <MediaBox state={videoState} label={signed.missing ? words : containerLabel("video", "filled", row.videoSeconds ?? 0)}
                  disabled={signed.missing} onMain={() => setOpen((o) => !o)}
                  onRemove={canRemove("video") && !signed.missing ? () => setConfirm("video") : null} removeLabel="Remove the video" />
      )}
      {row.photoPath !== null && (photo.missing
        ? <MediaBox state="removed" label={words} disabled />
        : <MediaBox state="filled" onRemove={canRemove("photo") ? () => setConfirm("photo") : null} removeLabel="Remove the photo">
            <button type="button" className="rm-photo" onClick={() => setPhotoOpen(true)} aria-label="Open the photo" disabled={photo.src === null}>
              {photo.src !== null && <img src={photo.src} alt="" />}
            </button>
          </MediaBox>)}
      {confirm !== null && (
        <p className="rm-confirm" role="alertdialog" aria-label="Remove">
          <span>Remove this {confirm === "voice" ? "voice note" : confirm === "video" ? "video" : "photo"}? {mine ? "They will see it was removed." : ""}</span>
          <button type="button" className="ht-button ht-button-quiet" onClick={() => void removeObject(confirm)}>Remove</button>
          <button type="button" className="ours-text-action" onClick={() => setConfirm(null)}>Keep</button>
        </p>
      )}
      {error !== null && <p className="review-quiet-line" role="alert">Not removed: {error}</p>}
      {open && row.videoPath !== null && !signed.missing && (stage
        ? <MediaStage shape="round" label="Your video" onClose={() => setOpen(false)}>
            <RoundVideoPlayer src={signed.src} seconds={row.videoSeconds ?? 0} unavailable={signed.failed ? "This video could not be fetched." : null} />
          </MediaStage>
        : <div className="rm-stage"><RoundVideoPlayer src={signed.src} seconds={row.videoSeconds ?? 0} unavailable={signed.failed ? "This video could not be fetched." : null} /></div>
      )}
      {photoOpen && photo.src !== null && (
        <MediaStage shape="photo" label="The photo" onClose={() => setPhotoOpen(false)}>
          <img className="media-stage-media" src={photo.src} alt="" />
        </MediaStage>
      )}
    </>
  );
}
